import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  CircleCheck,
  Rocket,
  Calendar,
  Phone,
  AlertCircle,
} from "lucide-react";
import { trackLead, trackInitiateCheckout } from "../lib/analytics";
import { sendToGHL } from "../lib/ghl";
import { useSEO } from "../lib/useSEO";

const RESIDENTIAL_OPTIONS = [
  { value: "yes", label: "Yes — that's exactly the work I want" },
  { value: "no", label: "No — that's not the work I want", disqualify: true },
];

const JOB_TYPES = [
  "House washing",
  "Roof cleaning",
  "Pressure washing",
  "Window cleaning",
  "Solar cleaning",
  "Gutter cleaning",
  "Pool cleaning",
  "Domestic cleaning",
];

const TIMEFRAME_OPTIONS = [
  { value: "right_away", label: "Right away", sub: "Ready to get started this week." },
  { value: "next_month", label: "Next month", sub: "Planning ahead for the next 30 days." },
  { value: "just_looking", label: "Just looking", sub: "Exploring options for now." },
];

const TOTAL_STEPS = 5;

const OfferPage = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const formCardRef = useRef(null);
  const [data, setData] = useState({
    residential: "",
    jobs: [],
    startTimeframe: "",
    area: "",
    website: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useSEO({
    title: "Check My Territory — 7 Figure Cleaner",
    description:
      "Take the 60-second qualification quiz. One exterior-cleaning business per territory across Australia — check if yours is still available.",
    path: "/offer",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel: user has entered the qualification funnel
    trackInitiateCheckout({ content_name: "Territory Check" });
  }, []);

  useEffect(() => {
    const card = formCardRef.current;
    if (!card) return;
    const HEADER_OFFSET = 80;
    const target = card.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }, [step, submitted]);

  const disqualified = data.residential === "no";
  const progress = useMemo(() => Math.min(100, (step / TOTAL_STEPS) * 100), [step]);

  const update = (key, value) => setData((d) => ({ ...d, [key]: value }));
  const toggleJob = (job) =>
    setData((d) => ({
      ...d,
      jobs: d.jobs.includes(job) ? d.jobs.filter((j) => j !== job) : [...d.jobs, job],
    }));

  const canContinue = () => {
    if (step === 1) return !!data.residential;
    if (step === 2) return data.jobs.length > 0;
    if (step === 3) return !!data.startTimeframe;
    if (step === 4) return data.area && data.website;
    if (step === 5) return data.firstName && data.lastName && data.email && data.phone;
    return false;
  };

  const next = (e) => {
    e?.preventDefault?.();
    if (!canContinue()) return;
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      const payload = { ...data, at: new Date().toISOString() };
      try {
        const queue = JSON.parse(localStorage.getItem("7fc_applications") || "[]");
        queue.push(payload);
        localStorage.setItem("7fc_applications", JSON.stringify(queue));
        localStorage.setItem("7fc_latest_application", JSON.stringify(payload));
      } catch (_) {}
      // GoHighLevel: push the lead into the CRM via webhook (fire & forget).
      sendToGHL({
        source: "7fc_territory_check",
        first_name: data.firstName,
        last_name: data.lastName,
        full_name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        phone: data.phone,
        residential: data.residential,
        services: data.jobs,
        services_csv: data.jobs.join(", "),
        start_timeframe: data.startTimeframe,
        service_area: data.area,
        website: data.website,
        submitted_at: payload.at,
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
      // Meta Pixel: territory check completed
      trackLead({
        content_name: "Territory Check Submitted",
        content_category: "Exterior Cleaning",
      });
      setSubmitted(true);
    }
  };

  const back = () => step > 1 && setStep(step - 1);

  return (
    <div className="flex min-h-screen flex-col overflow-x-clip bg-white text-brand-black">
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" className="flex items-center" aria-label="Back to home">
            <img
              src="/logo/logo-lg.webp"
              alt="The 7 Figure Cleaner logo"
              className="h-12 w-auto object-contain sm:h-[58px]"
              width="800"
              height="288"
            />
          </Link>
          <div className="hidden items-center gap-2 text-xs font-black uppercase tracking-wider text-brand-grayMid sm:flex">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Territory & Qualification Check
          </div>
        </div>
      </header>
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-14">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand-grayMid hover:text-brand-blue"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            {/* LEFT — Marketing copy */}
            <aside className="lg:col-span-5">
              <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-emerald-800">
                <ShieldCheck className="h-3.5 w-3.5" /> One Operator Per Area
              </p>
              <h1 className="text-4xl font-black uppercase leading-[1.02] tracking-tight text-brand-black sm:text-5xl">
                Check if Your <span className="text-brand-blue">Territory</span> Is Still Open.
              </h1>
              <p className="mt-5 text-base leading-relaxed text-[#374151] sm:text-lg">
                We only work with one exterior-cleaning operator per area. Tell us a bit about your business and we&apos;ll let you know if your territory is available and if we&apos;re the right fit to get you 30 exclusive local enquiries in 30 days.
              </p>
              <p className="mt-3 text-sm font-bold uppercase tracking-wider text-brand-blue">
                Zero contracts. Cancel anytime.
              </p>

              <ul className="mt-7 flex flex-col gap-3">
                {[
                  { Icon: ShieldCheck, t: "30 enquiries in 30 days, or we work free" },
                  { Icon: Rocket, t: "Live in 7 days from kick-off" },
                  { Icon: Calendar, t: "No setup fees. Cancel anytime." },
                ].map(({ Icon, t }) => (
                  <li key={t} className="flex items-center gap-3 text-sm font-medium text-brand-black sm:text-base">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                      <Icon className="h-4 w-4" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>

              <div className="mt-8 hidden lg:block">
                <img
                  src="https://customer-assets.emergentagent.com/job_ads-builder-8/artifacts/xfibtfnz_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2007_28_51%20AM.png"
                  alt="Guarantee badge"
                  className="h-44 w-auto"
                />
              </div>
            </aside>

            {/* RIGHT — Form */}
            <div className="lg:col-span-7">
              <div ref={formCardRef} className="rounded-3xl bg-white p-7 shadow-sm ring-1 ring-gray-200 sm:p-9 scroll-mt-24">
                {!submitted && !disqualified && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-black uppercase tracking-widest text-brand-grayMid">
                        Step {step} of {TOTAL_STEPS}
                      </span>
                      <span className="text-[11px] font-black uppercase tracking-widest text-brand-blue">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-brand-blue transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {submitted ? (
                  <div className="py-6 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                      <CircleCheck className="h-8 w-8" />
                    </div>
                    <h2 className="mt-6 text-3xl font-black uppercase tracking-tight text-brand-black sm:text-4xl">
                      Territory Check Submitted.
                    </h2>
                    <p className="mt-3 text-base leading-relaxed text-[#374151]">
                      Thanks {data.firstName || "mate"} — Chris will personally reach out within 24 hours to confirm whether your area is open and to walk you through the next steps.
                    </p>

                    <div className="mx-auto mt-8 max-w-md rounded-2xl bg-blue-50/60 p-5 ring-1 ring-brand-blue/20">
                      <p className="text-xs font-black uppercase tracking-widest text-brand-blue">
                        Don&apos;t want to wait?
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-brand-black">
                        Skip the wait and book a 30-minute call with Chris right now. Your details are already saved.
                      </p>
                      <Link
                        to="/booking"
                        className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-brand-blue px-6 py-4 text-sm font-black uppercase tracking-wider text-white shadow-xl shadow-blue-200/60 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99]"
                      >
                        Book a Call With Chris <Calendar className="h-5 w-5" />
                      </Link>
                    </div>

                    <Link
                      to="/"
                      className="mt-6 inline-flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand-grayMid hover:text-brand-blue"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                  </div>
                ) : disqualified ? (
                  <div className="py-2">
                    <div className="flex items-start gap-3 rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                      <p className="text-xs font-black uppercase tracking-widest text-amber-800">
                        Important
                      </p>
                    </div>
                    <h2 className="mt-6 text-2xl font-black uppercase leading-tight tracking-tight text-brand-black sm:text-3xl">
                      It looks like we might not be the right <span className="text-brand-blue">fit</span>.
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-[#374151]">
                      We specialise in <span className="font-bold text-brand-black">exterior cleaning</span> — house washing, roof washing and pressure cleaning. If that&apos;s not the type of work you want more of, our system probably isn&apos;t for you.
                    </p>
                    <p className="mt-3 text-base leading-relaxed text-[#374151]">
                      If you&apos;d still like to chat about your options, give us a call:
                    </p>
                    <a
                      href="tel:0437863673"
                      className="mt-6 inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-brand-blue px-7 py-4 text-base font-black uppercase tracking-wider text-white shadow-xl shadow-blue-200/60 transition-all hover:bg-blue-600"
                    >
                      <Phone className="h-5 w-5" /> 0437 863 673
                    </a>
                    <div className="mt-6">
                      <button
                        onClick={() => update("residential", "")}
                        className="text-xs font-black uppercase tracking-widest text-brand-grayMid hover:text-brand-blue"
                      >
                        ← Go back and change my answer
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={next}>
                    {step === 1 && <Step1 data={data} update={update} />}
                    {step === 2 && <Step2 data={data} toggleJob={toggleJob} />}
                    {step === 3 && <Step3 data={data} update={update} />}
                    {step === 4 && <Step4 data={data} update={update} />}
                    {step === 5 && <Step5 data={data} update={update} />}

                    <div className="mt-8 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={back}
                        disabled={step === 1}
                        className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-wider text-brand-grayMid transition-colors hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ArrowLeft className="h-4 w-4" /> Back
                      </button>
                      <button
                        type="submit"
                        disabled={!canContinue()}
                        className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-brand-blue px-7 py-4 text-sm font-black uppercase tracking-wider text-white shadow-xl shadow-blue-200/60 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand-blue"
                      >
                        {step === TOTAL_STEPS ? "Submit" : "Next"} <ArrowRight className="h-5 w-5" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer className="border-t border-gray-100 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-5 py-5 text-xs text-brand-grayMid sm:flex-row sm:px-8">
          <p>© {new Date().getFullYear()} The 7 Figure Cleaner.</p>
          <p className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Your information is private &amp; secure.
          </p>
        </div>
      </footer>
    </div>
  );
};

/* ============================
   Step components
   ============================ */

const Label = ({ children }) => (
  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-wider text-brand-grayMid">
    {children}
  </label>
);

const inputCls =
  "min-h-[48px] w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-base font-medium text-brand-black placeholder:text-gray-400 focus:border-brand-blue focus:outline-none sm:text-sm";

const OptionButton = ({ selected, onClick, children, sub }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-all ${
      selected
        ? "border-brand-blue bg-brand-blue/5"
        : "border-gray-200 bg-white hover:border-brand-blue/40"
    }`}
  >
    <div>
      <p className="text-sm font-black uppercase tracking-wide text-brand-black sm:text-base">
        {children}
      </p>
      {sub && <p className="text-xs text-brand-grayMid">{sub}</p>}
    </div>
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-2 ${
        selected ? "bg-brand-blue text-white ring-brand-blue" : "ring-gray-300"
      }`}
    >
      {selected && <CircleCheck className="h-4 w-4" />}
    </span>
  </button>
);

const Step1 = ({ data, update }) => (
  <div>
    <h2 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
      Do you want more house washing, roof washing or pressure-cleaning work in your service area?
    </h2>
    <p className="mt-2 text-sm text-brand-grayMid">Pick the option that best matches your business.</p>
    <div className="mt-6 flex flex-col gap-3">
      {RESIDENTIAL_OPTIONS.map((opt) => (
        <OptionButton
          key={opt.value}
          selected={data.residential === opt.value}
          onClick={() => update("residential", opt.value)}
        >
          {opt.label}
        </OptionButton>
      ))}
    </div>
  </div>
);

const Step2 = ({ data, toggleJob }) => (
  <div>
    <h2 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
      What type of cleaning jobs do you want more of?
    </h2>
    <p className="mt-2 text-sm text-brand-grayMid">Select all that apply.</p>
    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
      {JOB_TYPES.map((job) => {
        const selected = data.jobs.includes(job);
        return (
          <button
            type="button"
            key={job}
            onClick={() => toggleJob(job)}
            className={`flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-all ${
              selected
                ? "border-brand-blue bg-brand-blue/5"
                : "border-gray-200 bg-white hover:border-brand-blue/40"
            }`}
          >
            <span className="text-sm font-bold text-brand-black sm:text-base">{job}</span>
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ring-2 ${
                selected ? "bg-brand-blue text-white ring-brand-blue" : "ring-gray-300"
              }`}
            >
              {selected && <CircleCheck className="h-4 w-4" />}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const Step3 = ({ data, update }) => (
  <div>
    <h2 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
      If our system makes sense, how soon do you want to start?
    </h2>
    <p className="mt-2 text-sm text-brand-grayMid">Pick the option that fits best.</p>
    <div className="mt-6 flex flex-col gap-3">
      {TIMEFRAME_OPTIONS.map((opt) => (
        <OptionButton
          key={opt.value}
          selected={data.startTimeframe === opt.value}
          onClick={() => update("startTimeframe", opt.value)}
          sub={opt.sub}
        >
          {opt.label}
        </OptionButton>
      ))}
    </div>
  </div>
);

const Step4 = ({ data, update }) => (
  <div>
    <h2 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
      Let&apos;s check your territory.
    </h2>
    <p className="mt-2 text-sm text-brand-grayMid">
      We only work with one operator per area — tell us where you service so we can confirm availability.
    </p>
    <div className="mt-6 flex flex-col gap-4">
      <div>
        <Label>What area do you service?</Label>
        <textarea
          required
          rows={3}
          className={inputCls + " min-h-[96px] resize-y"}
          placeholder="Provide as much detail about your territory i.e. radius of city or suburbs."
          value={data.area}
          onChange={(e) => update("area", e.target.value)}
        />
      </div>
      <div>
        <Label>Business Website</Label>
        <input
          required
          className={inputCls}
          placeholder="https://yourbusiness.com.au"
          value={data.website}
          onChange={(e) => update("website", e.target.value)}
        />
      </div>
    </div>
  </div>
);

const Step5 = ({ data, update }) => (
  <div>
    <h2 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
      Last step — how do we reach you?
    </h2>
    <p className="mt-2 text-sm text-brand-grayMid">Chris will get back to you within 24 hours.</p>
    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <Label>First Name</Label>
        <input
          required
          className={inputCls}
          placeholder="Jane"
          value={data.firstName}
          onChange={(e) => update("firstName", e.target.value)}
        />
      </div>
      <div>
        <Label>Last Name</Label>
        <input
          required
          className={inputCls}
          placeholder="Smith"
          value={data.lastName}
          onChange={(e) => update("lastName", e.target.value)}
        />
      </div>
      <div>
        <Label>Business Email</Label>
        <input
          required
          type="email"
          className={inputCls}
          placeholder="you@business.com"
          value={data.email}
          onChange={(e) => update("email", e.target.value)}
        />
      </div>
      <div>
        <Label>Phone</Label>
        <input
          required
          className={inputCls}
          placeholder="0400 000 000"
          value={data.phone}
          onChange={(e) => update("phone", e.target.value)}
        />
      </div>
    </div>
    <p className="mt-4 text-xs text-brand-grayMid">
      By submitting, you agree to be contacted by 7 Figure Cleaner about your territory check.
    </p>
  </div>
);

export default OfferPage;
